// Centralized error reporting + product metrics via Sentry.
//
// Enabled ONLY when REACT_APP_SENTRY_DSN is set, so local dev without a DSN is a
// no-op. Set the DSN as an env var in Netlify (Site settings → Environment) and,
// for local testing, in pickledepot_user_web_portal/.env.local:
//   REACT_APP_SENTRY_DSN=https://<key>@<org>.ingest.sentry.io/<project>
//
// Optional env overrides (all tunable from Netlify WITHOUT a code change):
//   REACT_APP_SENTRY_ENV               — environment label (defaults to NODE_ENV)
//   REACT_APP_SENTRY_RELEASE           — release/version string
//   REACT_APP_SENTRY_TRACES_RATE       — perf-tracing sample rate 0–1  (default 0.1)
//   REACT_APP_SENTRY_REPLAY_RATE       — replay rate for healthy sessions (default 0)
//   REACT_APP_SENTRY_REPLAY_ERROR_RATE — replay rate when an error fires  (default 1)
//   REACT_APP_SENTRY_PROFILE_RATE      — CPU profiling sample rate 0–1   (default 0)
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

const DSN = process.env.REACT_APP_SENTRY_DSN;
export const sentryEnabled = Boolean(DSN);

// Parse an env-provided sample rate, clamped to a valid [0,1] probability.
// Falls back to `fallback` for anything missing/invalid.
const rate = (envVal, fallback) => {
  const n = Number.parseFloat(envVal);
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback;
};

if (sentryEnabled) {
  Sentry.init({
    dsn: DSN,
    environment:
      process.env.REACT_APP_SENTRY_ENV ||
      (process.env.NODE_ENV === "production" ? "production" : "development"),
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,

    integrations: [
      // Performance tracing wired to React Router v7: page-load + navigation
      // transactions named by route, Core Web Vitals (LCP/CLS/INP/FCP/TTFB),
      // and automatic timing spans for every fetch/XHR (incl. Supabase calls)
      // and resource load. This is what feeds the Performance/Web Vitals views.
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Session Replay — a video-like reconstruction of what the user did before
      // an error. Privacy is locked down: all text + form inputs are masked and
      // media blocked, so nothing sensitive (names, emails, payment fields)
      // leaves the browser.
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
      // CPU profiling (the flame-graph view). Inert unless PROFILE_RATE > 0 AND
      // the `Document-Policy: js-profiling` response header is served (see
      // public/_headers). Zero overhead while the rate is 0.
      Sentry.browserProfilingIntegration(),
    ],

    // Release Health: track sessions so Sentry can report crash-free rate.
    autoSessionTracking: true,

    // % of page-loads/navigations sampled for performance tracing. 10% keeps us
    // inside the free-plan span quota (~10k/mo). Raise it via
    // REACT_APP_SENTRY_TRACES_RATE if you have headroom; lower it if you approach
    // the cap.
    tracesSampleRate: rate(process.env.REACT_APP_SENTRY_TRACES_RATE, 0.1),

    // Session Replay quota is tiny on the free plan (50/mo), so DON'T record
    // random healthy sessions — only capture a replay when an error fires, the
    // 50 most useful recordings you can spend. Both rates are env-overridable.
    replaysSessionSampleRate: rate(process.env.REACT_APP_SENTRY_REPLAY_RATE, 0),
    replaysOnErrorSampleRate: rate(
      process.env.REACT_APP_SENTRY_REPLAY_ERROR_RATE,
      1.0
    ),

    // CPU profiling sample rate (relative to traced transactions). Off by
    // default — opt in with REACT_APP_SENTRY_PROFILE_RATE and the Document-Policy
    // header in public/_headers.
    profilesSampleRate: rate(process.env.REACT_APP_SENTRY_PROFILE_RATE, 0),

    // IMPORTANT — do NOT add the Supabase host to tracePropagationTargets.
    // The edge-function CORS allow-list is `authorization, x-client-info, apikey,
    // content-type` and does NOT permit the `sentry-trace`/`baggage` headers, so
    // propagating distributed-trace headers there would fail CORS preflight and
    // break every functions.invoke call. We keep Sentry's safe same-origin
    // default; client-side timing spans for Supabase calls are still recorded
    // without propagation. (To enable true frontend↔backend distributed tracing
    // later, first add `sentry-trace, baggage` to the edge-function CORS headers
    // and instrument the functions with Sentry.)

    // Known noise that is not actionable from our code:
    // - "window.webkit.messageHandlers": scripts injected into the page by iOS
    //   in-app browsers / WebView wrappers (Instagram, Facebook, Google app…)
    //   reference an API that only exists inside a real WKWebView. The throwing
    //   frames (setupIosCallbackHandler / sendDataToNative / sendPageHideMessage)
    //   are injected code, not part of our bundle.
    // - "Java exception was raised during method invocation" /
    //   "Error invoking postMessage": the ANDROID half of the same story. Meta's
    //   in-app browser injects `navigation_performance_logger_android` (served
    //   over its private `iabjs://` scheme), which posts navigation timings to the
    //   native app over a WebView JS bridge; the bridge throws when the page is
    //   already unloading. It surfaces in our stacks only because the beforeunload
    //   that triggers it is the one signInWithOAuth causes, and because Sentry
    //   wraps every addEventListener callback — the throw happens in the injected
    //   listener and never reaches our code, so the OAuth redirect still proceeds.
    // - "browsing context is going away": WebKit rejects in-flight promises
    //   (fetch, auth redirects) when the user navigates away or closes the tab.
    //   Expected during any page transition; nothing to fix.
    // - "InvalidJWTToken": supabase-js realtime raises an unhandled rejection
    //   when a backgrounded tab wakes up with an expired access token; the
    //   client refreshes the token and reconnects on its own.
    ignoreErrors: [
      "window.webkit.messageHandlers",
      "Java exception was raised during method invocation",
      "Error invoking postMessage",
      "browsing context is going away",
      "InvalidJWTToken",
    ],

    // Drop anything thrown by a script that is not ours. `iabjs://` is the scheme
    // Meta's in-app browser serves its injected instrumentation from, so a stack
    // topped by that file is by definition not our bug. Matched against the
    // throwing frame's filename, unlike ignoreErrors which matches the message.
    denyUrls: [/^iabjs:\/\//],

    sendDefaultPii: false,
  });
}

// Keys to redact before attaching a request snapshot to an error report, so we
// never ship PII (names, contact details, credentials) to Sentry.
const REDACT_KEYS = new Set([
  "billing_name", "billing_email", "billing_mobile", "billing_address",
  "bill_name", "bill_email", "bill_mobile",
  "email", "phone", "full_name", "name",
  "password", "jwt", "token", "access_token",
]);

const sanitize = (value, depth = 0) => {
  if (value == null || depth > 4) return value;
  if (Array.isArray(value)) return value.map((v) => sanitize(v, depth + 1));
  if (typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = REDACT_KEYS.has(k) ? "[redacted]" : sanitize(v, depth + 1);
    }
    return out;
  }
  return value;
};

// Turn an arbitrary thrown value into a real Error before handing it to Sentry.
//
// WHY: supabase-js does NOT reject — it resolves with `{ data, error }`, and for
// PostgREST that `error` is a PLAIN OBJECT ({ code, message, details, hint }),
// not an Error instance. Sentry.captureException(plainObject) cannot read a
// message or a stack off it, so it files the event as
//
//   "Object captured as exception with keys: code, dataLoadStep, details, hint, message"
//
// — which is what every PostgREST failure looked like in Sentry: one title
// carrying no information, and (because the title IS the grouping key) all four
// reference-data steps and every possible pg error collapsed into a single issue.
// Building a real Error restores both the title and per-step/per-code grouping.
// `fingerprint` pins the grouping explicitly so it survives future title edits.
const toReportableError = (error, fallbackName, prefix) => {
  if (error instanceof Error) return error;
  const message = error?.message || error?.error_description || String(error);
  const normalized = new Error(prefix ? `${prefix}: ${message}` : message);
  normalized.name = error?.name || fallbackName;
  return normalized;
};

// Attach the signed-in user to all subsequent events (call on auth state change).
export const setSentryUser = (user) => {
  if (!sentryEnabled) return;
  Sentry.setUser(user ? { id: user.id, email: user.email } : null);
};

// Report a failed supabase.functions.invoke(...) call with rich context.
// `error` may be a FunctionsFetchError ("Failed to send a request to the Edge
// Function" — no readable CORS response / worker crash) or a FunctionsHttpError
// (a non-2xx response with a body). Both are worth capturing.
export const reportEdgeFunctionError = (functionName, error, options) => {
  if (!sentryEnabled || !error) return;
  try {
    const status = error?.context?.status ?? null;
    // FunctionsFetchError/FunctionsHttpError ARE Error subclasses, so this is
    // normally a pass-through; it only matters if a caller hands us a raw object.
    Sentry.captureException(
      toReportableError(error, "FunctionsError", functionName),
      {
        tags: {
          edge_function: functionName,
          error_type: error?.name || "FunctionsError",
          http_status: status ?? "none",
        },
        fingerprint: ["edge-function", functionName, String(status ?? "none")],
        extra: {
          functionName,
          status,
          message: error?.message,
          requestBody: sanitize(options?.body),
        },
      }
    );
  } catch (_) {
    // Never let error reporting throw inside the app.
  }
};

// Report a failed Supabase TABLE read/write (`supabase.from(...)`).
//
// These bypass the functions.invoke wrapper in supabase.js entirely, which is why
// this class of failure used to be invisible everywhere at once: PostgREST reads
// never appear in the Edge Function logs, and an error caught by a try/catch never
// reaches Sentry's global handler. `step` names the table/stage that failed so the
// event is identifiable without a stack trace.
export const reportDataLoadError = (step, error, meta = {}) => {
  if (!sentryEnabled || !error) return;
  try {
    const code = error?.code || "none";
    Sentry.captureException(toReportableError(error, "PostgrestError", step), {
      tags: {
        data_load_step: step,
        error_type: error?.name || "PostgrestError",
        pg_code: code,
        http_status: error?.status ?? error?.context?.status ?? "none",
      },
      fingerprint: ["data-load", step, code],
      extra: {
        step,
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        ...meta,
      },
    });
  } catch (_) {
    // Never let error reporting throw inside the app.
  }
};
